import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Crown, ShieldOff, TrendingUp, UserPlus, UserMinus, Activity } from 'lucide-react';
import { contactsService } from 'api/services/contacts.service';
import { useAuth } from 'hooks/useAuth';

interface ContactStats {
  total: number;
  vip: number;
  blocked: number;
  recentlyAdded: number;
  activeThisMonth: number;
  growthRate: number;
}

const UserDirectoryOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [stats, setStats] = useState<ContactStats>({
    total: 0,
    vip: 0,
    blocked: 0,
    recentlyAdded: 0,
    activeThisMonth: 0,
    growthRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchContactStats();
  }, [currentOrganization]);

  const fetchContactStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call real API
      const response = await contactsService.getStats(currentOrganization?.id);

      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setError(response.error || 'Failed to fetch contact statistics');
        console.error('Failed to fetch contact stats:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch contact stats:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Contacts',
      value: stats.total,
      icon: Users,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'All contacts in directory',
      action: () => navigate('/user-directory/contacts'),
    },
    {
      title: 'VIP Contacts',
      value: stats.vip,
      icon: Crown,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      description: 'Priority contacts',
      action: () => navigate('/user-directory/vip-management'),
    },
    {
      title: 'Blocked Numbers',
      value: stats.blocked,
      icon: ShieldOff,
      color: 'bg-red-500',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50',
      description: 'Numbers in DND lists',
      action: () => navigate('/user-directory/dnd-management'),
    },
    {
      title: 'Recently Added',
      value: stats.recentlyAdded,
      icon: UserPlus,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'Added in last 7 days',
      action: () => navigate('/user-directory/contacts'),
    },
    {
      title: 'Active This Month',
      value: stats.activeThisMonth,
      icon: Activity,
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Contacts with activity',
      action: () => navigate('/user-directory/contacts'),
    },
    {
      title: 'Growth Rate',
      value: `${stats.growthRate >= 0 ? '+' : ''}${stats.growthRate}%`,
      icon: TrendingUp,
      color: 'bg-indigo-500',
      textColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      description: 'vs. last month',
      action: () => navigate('/user-directory/contacts'),
    },
  ];

  const quickActions = [
    {
      title: 'Manage Contacts',
      description: 'View and edit all contacts',
      icon: Users,
      color: 'bg-blue-500',
      action: () => navigate('/user-directory/contacts'),
    },
    {
      title: 'VIP Lists',
      description: 'Manage priority caller lists',
      icon: Crown,
      color: 'bg-yellow-500',
      action: () => navigate('/user-directory/vip-management'),
    },
    {
      title: 'DND Lists',
      description: 'Manage blocked number lists',
      icon: ShieldOff,
      color: 'bg-red-500',
      action: () => navigate('/user-directory/dnd-management'),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="animate-pulse">
          <div className="h-6 lg:h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4 lg:mb-6"></div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-28 lg:h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 dark:bg-red-800 rounded-full p-2">
              <Activity className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-200">Failed to Load Statistics</h3>
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div>
        <h1 className="text-xl lg:text-3xl font-bold text-gray-900 dark:text-white">User Directory</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Overview of contacts, VIP lists, and blocked numbers
        </p>
      </div>

      {/* Statistics Cards - 2 columns on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              onClick={card.action}
              className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-3 lg:mb-4">
                <div className={`p-2 lg:p-3 rounded-lg ${card.bgColor} dark:opacity-80`}>
                  <Icon className={`w-5 h-5 lg:w-6 lg:h-6 ${card.textColor}`} />
                </div>
                <div className={`px-1.5 lg:px-2 py-0.5 lg:py-1 rounded text-xs font-semibold ${card.color} text-white hidden sm:block`}>
                  Live
                </div>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-xs lg:text-sm font-medium truncate">{card.title}</p>
                <p className="text-xl lg:text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                </p>
                <p className="text-gray-500 dark:text-gray-500 text-xs mt-1 lg:mt-2 hidden sm:block">{card.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions - Single column on mobile */}
      <div>
        <h2 className="text-base lg:text-xl font-semibold text-gray-900 dark:text-white mb-3 lg:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={action.action}
                className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow p-4 lg:p-6 border border-gray-200 dark:border-gray-700 text-left group flex sm:flex-col items-center sm:items-start gap-3 sm:gap-0"
              >
                <div className={`inline-flex p-2 lg:p-3 rounded-lg ${action.color} sm:mb-3 group-hover:scale-110 transition-transform flex-shrink-0`}>
                  <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm lg:text-lg font-semibold text-gray-900 dark:text-white mb-0.5 lg:mb-1">{action.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs lg:text-sm">{action.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent activity — only what the backend actually measures.
          This block previously rendered four invented figures ("23 new contacts
          added", "12 added to VIP lists", "5 numbers blocked", "3 contacts
          removed"), sitting directly beneath the real stat cards in identical
          styling, so nothing on screen distinguished measured from imagined.
          /contacts/stats supplies exactly one genuine recent-activity figure —
          recentlyAdded, which the backend computes as countCreatedSince(now-7d),
          so the "last 7 days" caption is accurate for it and nothing else. VIP
          and blocked counts are lifetime totals already shown above, not
          deltas, and contact removals are not tracked at all. Rather than
          dress those up as activity, they are absent and said to be absent. */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-base lg:text-xl font-semibold text-gray-900 dark:text-white mb-3 lg:mb-4">Recent Activity</h2>
        <div className="space-y-2 lg:space-y-3">
          <div className="flex items-center gap-3 p-2 lg:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
              <UserPlus className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white truncate">
                {stats.recentlyAdded.toLocaleString()} new {stats.recentlyAdded === 1 ? 'contact' : 'contacts'} added
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">In the last 7 days</p>
            </div>
          </div>

          {/* growthRate compares the last 30 days with the 30 before it. Shown
              only when there is a prior period to compare against — otherwise
              the backend returns 0, which would read as "no growth" rather than
              "nothing to compare yet". */}
          {stats.growthRate !== 0 && (
            <div className="flex items-center gap-3 p-2 lg:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white truncate">
                  {stats.growthRate > 0 ? '+' : ''}{stats.growthRate}% growth
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Last 30 days vs the 30 before</p>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
            A per-event history — who was added, changed or blocked and when — is not recorded yet,
            so only these totals are available.
          </p>
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 lg:p-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-100 dark:bg-blue-800 rounded-full p-1.5 lg:p-2 flex-shrink-0">
            <Activity className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm lg:text-base text-blue-900 dark:text-blue-200 mb-1">User Directory</h3>
            <p className="text-xs lg:text-sm text-blue-800 dark:text-blue-300">
              Manage all your contacts, VIP caller lists, and blocked numbers in one place.
              <span className="hidden sm:inline"> Use VIP lists to provide priority routing for important callers, and DND lists
              to block unwanted numbers.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDirectoryOverview;
